import React from 'react';

/**
 * Sanitizes a URL to prevent XSS attacks
 */
const sanitizeUrl = (url: string): string => {
  const trimmedUrl = url.trim();
  
  // Only allow http, https, and mailto protocols
  if (
    trimmedUrl.startsWith('http://') ||
    trimmedUrl.startsWith('https://') ||
    trimmedUrl.startsWith('mailto:')
  ) {
    return trimmedUrl;
  }
  
  // If no protocol, assume https
  if (!trimmedUrl.includes('://')) {
    return `https://${trimmedUrl}`;
  }
  
  // Block any other protocols (javascript:, data:, etc.)
  return '#';
};

/**
 * Parses text and converts markdown links [text](url) and plain URLs into clickable links
 */
export const parseLinksInText = (text: string): React.ReactNode[] => {
  if (!text) return [text];

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  
  // Regex to match markdown links [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  // Regex to match plain URLs
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  
  // First, find all markdown links
  const markdownMatches = Array.from(text.matchAll(markdownLinkRegex));
  const urlMatches = Array.from(text.matchAll(urlRegex));
  
  // Combine and sort all matches by their position
  const allMatches = [
    ...markdownMatches.map(m => ({ type: 'markdown' as const, match: m, index: m.index! })),
    ...urlMatches.map(m => ({ type: 'url' as const, match: m, index: m.index! }))
  ].sort((a, b) => a.index - b.index);
  
  // Filter out overlapping matches (prefer markdown over plain URLs)
  const filteredMatches = allMatches.filter((match, i) => {
    // Check if this match overlaps with any previous markdown match
    for (let j = 0; j < i; j++) {
      const prevMatch = allMatches[j];
      if (prevMatch.type === 'markdown') {
        const prevEnd = prevMatch.index + prevMatch.match[0].length;
        const currentStart = match.index;
        const currentEnd = match.index + match.match[0].length;
        
        // If current match is inside a previous markdown match, filter it out
        if (currentStart >= prevMatch.index && currentStart < prevEnd) {
          return false;
        }
        if (currentEnd > prevMatch.index && currentEnd <= prevEnd) {
          return false;
        }
      }
    }
    return true;
  });
  
  filteredMatches.forEach((matchInfo, index) => {
    const { type, match } = matchInfo;
    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;
    
    // Add text before the match
    if (matchStart > lastIndex) {
      parts.push(text.substring(lastIndex, matchStart));
    }
    
    if (type === 'markdown') {
      // Markdown link: [text](url)
      const linkText = match[1];
      const url = match[2];
      const sanitizedUrl = sanitizeUrl(url);
      
      parts.push(
        <a
          key={`link-${index}`}
          href={sanitizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline font-medium break-words"
        >
          {linkText}
        </a>
      );
    } else {
      // Plain URL
      const url = match[0];
      const sanitizedUrl = sanitizeUrl(url);
      
      parts.push(
        <a
          key={`link-${index}`}
          href={sanitizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {url}
        </a>
      );
    }
    
    lastIndex = matchEnd;
  });
  
  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : [text];
};